/**
 * Admin → Field Settings API calls.
 *
 * The screen and this module must never name a de-hub module or a database
 * table — that is what lets one implementation serve every app built from
 * this template, rather than a copy per app drifting apart.
 *
 * The contract each module implements:
 *
 *   GET  {base}/metadata/tables        -> { data: ["deals", ...] }
 *   GET  {base}/metadata?table_name=X  -> { data: [row, ...] }
 *   PUT  {base}/metadata/{id}          -> { data: [row] }
 *   GET  {base}/metadata/audit         -> { data: [entry, ...] }
 *
 * MORE THAN ONE SOURCE IMPLEMENTS IT. The app's own tables and the shared
 * App Support tables live in different de-hub modules and different databases,
 * so every call takes a `source` (see `METADATA_SOURCES` in
 * config/metadataAdmin.js) rather than reading one base from the environment.
 *
 * The only difference between sources is `app`: App Support's rows are shared
 * by every WIG front end and must be scoped, the app's own are not. Folding
 * that in here keeps it out of all four call sites and out of the screen.
 */
import dataService from "./dataservice";
import { clearingAfter, metadataCache } from "./referenceCache";
import { METADATA_SOURCES, sourceByKey } from "../config/metadataAdmin";

/** The default source — the app's own metadata. */
const DEFAULT_SOURCE = METADATA_SOURCES[0];

const resolve = (source) =>
  typeof source === "string" ? sourceByKey(source) : source || DEFAULT_SOURCE;

const metadataPath = (source, suffix = "") =>
  `${resolve(source).base}/metadata${suffix}`;

/**
 * Add `app` when the source is shared. Omitted entirely otherwise — the app's
 * own module rejects unknown query parameters, so sending a null would be a
 * 422 rather than a no-op.
 */
const scoped = (source, params = {}) => {
  const { app } = resolve(source);
  return app ? { app, ...params } : params;
};

/** The tables that have metadata rows, for the screen's table picker. */
const listTables = ({ token, userOid, source }) =>
  dataService.request("get", metadataPath(source, "/tables"), token, userOid, undefined, {
    params: scoped(source),
  });

/** Every metadata row for one table, ordered by `ord`. */
const listMetadata = ({ token, userOid, tableName, source }) =>
  dataService.request("get", metadataPath(source), token, userOid, undefined, {
    params: scoped(source, { table_name: tableName }),
  });

/**
 * Edit one row. `updates` carries only the properties being changed — the
 * server distinguishes "not sent" from "sent as null", so omitting a field
 * leaves it alone while sending null clears it.
 *
 * No `app` parameter: the row id already identifies the row, and accepting one
 * would invite a mismatch between the id and the scope.
 */
// Clears the app's cached metadata (services/referenceCache) so the admin
// sees the change on the next screen they open.
const updateMetadata = ({ token, userOid, id, updates, source }) =>
  clearingAfter(
    metadataCache,
    dataService.request(
      "put",
      metadataPath(source, `/${encodeURIComponent(id)}`),
      token,
      userOid,
      updates
    )
  );

/** Recent changes, newest first. Optionally narrowed to one field. */
const listAudit = ({ token, userOid, tableName, field, limit, source }) => {
  const params = {};
  if (tableName) params.table_name = tableName;
  if (field) params.field = field;
  if (limit !== undefined) params.limit = limit;
  return dataService.request(
    "get",
    metadataPath(source, "/audit"),
    token,
    userOid,
    undefined,
    { params: scoped(source, params) }
  );
};

const metadataAdminService = {
  listTables,
  listMetadata,
  updateMetadata,
  listAudit,
};

export default metadataAdminService;
